import { Component, ChangeDetectionStrategy, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="container mx-auto px-4 py-8 md:py-10">
  <div class="mx-auto max-w-5xl space-y-6">
    <section class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900 p-6 text-white shadow-elevated md:p-8">
      <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,.15),transparent_50%)]" aria-hidden="true"></div>
      <div class="relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-300/90">Conta</p>
          <h1 class="mt-2 text-2xl font-bold tracking-tight md:text-3xl">Perfil do usuário</h1>
          <p class="mt-1.5 text-sm text-slate-300">Dados da conta, assinatura e privacidade em um único lugar.</p>
        </div>
        <button
          type="button"
          (click)="backToApp.emit()"
          class="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/20">
          Voltar ao app
        </button>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <article class="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card">
        <h2 class="text-base font-bold tracking-tight text-slate-900">Informações da conta</h2>
        <dl class="mt-4 space-y-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-slate-400">Nome</dt>
            <dd class="font-semibold text-slate-800">{{ currentUser()?.name || '-' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-slate-400">E-mail</dt>
            <dd class="truncate font-semibold text-slate-800">{{ currentUser()?.email || '-' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-slate-400">Plano</dt>
            <dd class="font-semibold text-slate-800">{{ getPlanLabel(currentUser()?.plan || 'gratuito') }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-slate-400">Créditos</dt>
            <dd class="font-semibold text-slate-800">@if (credits() > 900) { Ilimitado } @else { {{ credits() }} }</dd>
          </div>
        </dl>
      </article>

      <article class="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card">
        <h2 class="text-base font-bold tracking-tight text-slate-900">Privacidade e LGPD</h2>
        <p class="mt-1 text-sm text-slate-400">Solicitações de privacidade ficam centralizadas nesta área.</p>

        <div class="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p class="text-sm font-semibold text-blue-700">Exportar meus dados</p>
          <p class="mt-1 text-xs text-blue-600">Baixe uma cópia de todos os seus dados pessoais conforme a LGPD.</p>

          <button
            type="button"
            (click)="onExportData()"
            [disabled]="isExportingData()"
            class="mt-3 w-full rounded-xl border border-blue-300 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-60">
            @if (isExportingData()) {
              Exportando...
            } @else {
              Exportar dados
            }
          </button>
        </div>

        <div class="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <p class="text-sm font-semibold text-rose-700">Excluir conta</p>
          <p class="mt-1 text-xs text-rose-600">A exclusão é permanente e remove seu acesso e dados vinculados.</p>

          <button
            type="button"
            (click)="onDeleteAccount()"
            [disabled]="isDeletingAccount()"
            class="mt-3 w-full rounded-xl border border-rose-300 bg-white px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60">
            {{ pendingDeleteConfirmation() ? 'Confirmar exclusão definitiva' : 'Excluir minha conta' }}
          </button>
        </div>
      </article>
    </section>
  </div>
</div>
`,
})
export class ProfilePageComponent {
  private authService = inject(AuthService);

  readonly currentUser = this.authService.currentUser;
  readonly credits = this.authService.effectiveCredits;
  readonly pendingDeleteConfirmation = signal(false);
  readonly isDeletingAccount = signal(false);
  readonly isExportingData = signal(false);

  accountDeleted = output<void>();
  backToApp = output<void>();

  getPlanLabel(plan: 'gratuito' | 'basico' | 'intermediario' | 'pro'): string {
    if (plan === 'basico') return 'Concurseiro Básico';
    if (plan === 'intermediario') return 'Concurseiro Intermediário';
    if (plan === 'pro') return 'Concurseiro Pro';
    return 'Gratuito';
  }

  async onExportData() {
    this.isExportingData.set(true);
    try {
      await this.authService.exportMyData();
      this.authService.showNotification('success', 'Dados exportados', 'O download do arquivo foi iniciado.');
    } catch (error: any) {
      this.authService.showNotification(
        'error',
        'Falha na exportação',
        error?.message ?? 'Não foi possível exportar seus dados agora.'
      );
    } finally {
      this.isExportingData.set(false);
    }
  }

  async onDeleteAccount() {
    if (!this.pendingDeleteConfirmation()) {
      this.pendingDeleteConfirmation.set(true);
      this.authService.showNotification(
        'info',
        'Confirmação necessária',
        'Clique novamente em "Excluir minha conta" para confirmar.'
      );
      return;
    }

    this.isDeletingAccount.set(true);
    try {
      await this.authService.deleteMyAccount();
      this.authService.showNotification('success', 'Conta excluída', 'Sua conta foi removida com sucesso.');
      this.accountDeleted.emit();
    } catch (error: any) {
      this.authService.showNotification(
        'error',
        'Falha ao excluir conta',
        error?.message ?? 'Não foi possível excluir sua conta agora.'
      );
    } finally {
      this.pendingDeleteConfirmation.set(false);
      this.isDeletingAccount.set(false);
    }
  }
}
